import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ECommerceTooltips } from "@omni/types/components/product/eCommerce/eCommerce.type";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useEffect, useState } from "react";

import { addProductToRecentlyViewed } from "@/services/recentlyViewedProducts.service";

import { OnAddToCartHandler } from "../actionHandlers/onAddToCart.handler";
import { OnSubmitHandler } from "../actionHandlers/onSubmit.handler";

type ECommerceContextValue = {
    product: Product<ProductFieldSet.Full>;
    onSubmitHandler: OnSubmitHandler;
    onAddToCartHandler: OnAddToCartHandler;
    xfPath: string | null;
    tooltips: ECommerceTooltips;
    infoLink: string;
    cashbackPagePath: string;
    paintConfiguratorPath: string | null;
    isEcommerceReady: boolean;
    setInitialised: React.Dispatch<React.SetStateAction<boolean>>;
};

export type ECommerceContextProviderProps = {
    product: Product<ProductFieldSet.Full>;
    tooltips: ECommerceTooltips;
    infoLink: string;
    contextAwareXfPath: string | null;
    cashbackPagePath: string;
    paintConfiguratorPath: string | null;
    handlers: {
        onSubmit: OnSubmitHandler;
        onAddToCart: OnAddToCartHandler;
    };
};

export const ECommerceContext = createContext<
    ECommerceContextValue | undefined
>(undefined);

ECommerceContext.displayName = "ECommerceContext";

export const ECommerceContextProvider = ({
    children,
    product,
    tooltips,
    infoLink,
    contextAwareXfPath,
    cashbackPagePath,
    paintConfiguratorPath,
    handlers: { onSubmit: onSubmitHandler, onAddToCart: onAddToCartHandler }
}: RequireChildrenFor<ECommerceContextProviderProps>) => {
    const [initialised, setInitialised] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        addProductToRecentlyViewed(product.sku);
    }, []);

    return (
        <ECommerceContext
            value={{
                product,
                onSubmitHandler,
                onAddToCartHandler,
                xfPath: contextAwareXfPath,
                tooltips,
                infoLink,
                cashbackPagePath,
                paintConfiguratorPath,
                isEcommerceReady: initialised,
                setInitialised
            }}
        >
            {children}
        </ECommerceContext>
    );
};

export const useECommerceContext = (): ECommerceContextValue => {
    const context = use<ECommerceContextValue | undefined>(ECommerceContext);
    if (!isDefined(context)) {
        throw new Error(`${ECommerceContext.displayName} is undefined.`);
    }
    return context;
};
